import { action, computed, makeObservable, observable } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsRequestDto, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface TrackingLogEntry {
	id: string;
	bnr_id: number;
	bensl: string;
	action: string;
	action_date: string;
	name: string;
	steart: number;
	vd: string;
	gs: string;
}

export interface TrackingLogAggRow {
	period: string;
	logins: number;
	login_fails: number;
	forgot_password: number;
	fitness_pdf: number;
	fitness_pdf_error: number;
	pdf_gen: number;
	pdf_error: number;
	send_count: number;
	nav_count: number;
}

const actionCaseCols = (aggFunc: AggregateFunction): SelectColumn[] => [
	{ caseExpression: { whenCondition: "action_type = 'login'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'logins' },
	{ caseExpression: { whenCondition: "action_type LIKE 'login_failed%'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'login_fails' },
	{ caseExpression: { whenCondition: "action_type LIKE 'forgotpassword%'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'forgot_password' },
	{ caseExpression: { whenCondition: "action_type = 'fitnesspdf'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'fitness_pdf' },
	{ caseExpression: { whenCondition: "action_type = 'fitnesspdf_error'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'fitness_pdf_error' },
	{ caseExpression: { whenCondition: "action_type = 'pdfgen'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'pdf_gen' },
	{ caseExpression: { whenCondition: "action_type = 'pdfgenerror'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'pdf_error' },
	{ caseExpression: { whenCondition: "action_type LIKE 'send%'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'send_count' },
	{ caseExpression: { whenCondition: "action_type = 'nav'", thenValue: '1', elseValue: '' }, aggregateFunction: aggFunc, alias: 'nav_count' },
];

export interface LoginFailedLeaderRow {
	bensl: string;
	name: string;
	vd: string;
	login_fails: number;
}

export interface NavLeaderboardRow {
	nav_clean: string;
	nav_count: number;
}

export interface BnrActionCountRow {
	bnr_id: number;
	action_count: number;
}

/** Sanitize a value for use in SQL string literals: strip everything except safe chars */
function sanitize(val: string): string {
	return val.replace(/[^a-zA-Z0-9äöüÄÖÜß _.@\-]/g, '').trim();
}

export class AdminLogStore extends AgtAnalyticsStore {
	@observable items: TrackingLogEntry[] = [];
	@observable byDay: TrackingLogAggRow[] = [];
	@observable byMonth: TrackingLogAggRow[] = [];
	@observable byHour: TrackingLogAggRow[] = [];
	@observable loginFailedLeaderboard: LoginFailedLeaderRow[] = [];
	@observable navLeaderboard: NavLeaderboardRow[] = [];
	@observable actionTypes: string[] = [];

	// Track which filter produced the cached data
	@observable loadedForFilter: string = '';
	@observable byDayLoadedForFilter: string = '';
	@observable byMonthLoadedForFilter: string = '';
	@observable byHourLoadedForFilter: string = '';
	@observable leaderboardLoadedForFilter: string = '';
	@observable navLeaderboardLoadedForFilter: string = '';

	// Filters
	@observable filterName: string = '';
	@observable filterSteart: string = '';
	@observable filterActionType: string = '';
	@observable filterAction: string = '';
	@observable filterVd: string = '';
	@observable filterGs: string = '';
	@observable filterDateFrom: string = '';
	@observable filterDateTo: string = '';

	constructor(session: SessionStore) {
		super(session, {
			tableName: 'tracking_log',
			defaultOrderBy: 'action_date',
			defaultOrderDirection: 'DESC',
			defaultLimit: 10000,
		});
		makeObservable(this);
		this.queryName = '?adminlog';
	}

	@computed
	get filterKey(): string {
		return `${this.filterName}|${this.filterSteart}|${this.filterActionType}|${this.filterAction}|${this.filterVd}|${this.filterGs}|${this.filterDateFrom}|${this.filterDateTo}`;
	}

	@computed
	get loaded(): boolean {
		return this.loadedForFilter === this.filterKey;
	}

	@computed
	get byDayLoaded(): boolean {
		return this.byDayLoadedForFilter === this.filterKey;
	}

	@computed
	get byMonthLoaded(): boolean {
		return this.byMonthLoadedForFilter === this.filterKey;
	}

	@computed
	get byHourLoaded(): boolean {
		return this.byHourLoadedForFilter === this.filterKey;
	}

	@computed
	get leaderboardLoaded(): boolean {
		return this.leaderboardLoadedForFilter === this.filterKey;
	}

	@computed
	get navLeaderboardLoaded(): boolean {
		return this.navLeaderboardLoadedForFilter === this.filterKey;
	}

	@computed
	get filterWhere(): string | undefined {
		const clauses: string[] = [];

		if (this.filterName) {
			const s = sanitize(this.filterName);
			if (s) {
				clauses.push(`(bensl ILIKE '%${s}%' OR name ILIKE '%${s}%' OR CAST(bnr_id AS TEXT) LIKE '%${s}%')`);
			}
		}
		if (this.filterSteart) {
			const s = sanitize(this.filterSteart);
			if (s) {
				clauses.push(`steart = ${parseInt(s, 10)}`);
			}
		}
		if (this.filterActionType) {
			const actions = this.filterActionType.split(',').map((a) => sanitize(a)).filter(Boolean);
			if (actions.length > 0) {
				const quoted = actions.map((a) => `'${a}'`).join(', ');
				clauses.push(`action_type IN (${quoted})`);
			}
		}
		if (this.filterAction) {
			const s = sanitize(this.filterAction);
			if (s) {
				clauses.push(`action ILIKE '%${s}%'`);
			}
		}
		if (this.filterVd) {
			const s = sanitize(this.filterVd);
			if (s) {
				clauses.push(`vd = '${s}'`);
			}
		}
		if (this.filterGs) {
			const s = sanitize(this.filterGs);
			if (s) {
				clauses.push(`gs = '${s}'`);
			}
		}
		if (this.filterDateFrom) {
			clauses.push(`action_date >= '${this.filterDateFrom}'`);
		}
		if (this.filterDateTo) {
			clauses.push(`action_date <= '${this.filterDateTo}'`);
		}

		return clauses.length > 0 ? clauses.join(' AND ') : undefined;
	}

	@action
	setFilters(name: string, steart: string, actionType: string, action: string, vd: string, gs: string, dateFrom: string, dateTo: string) {
		this.filterName = name;
		this.filterSteart = steart;
		this.filterActionType = actionType;
		this.filterAction = action;
		this.filterVd = vd;
		this.filterGs = gs;
		this.filterDateFrom = dateFrom;
		this.filterDateTo = dateTo;
	}

	@action
	async load() {
		if (this.loaded) return this.items;

		const keyAtStart = this.filterKey;
		const selectColumns: SelectColumn[] = [
			{ column: 'id', aggregateFunction: AggregateFunction.NONE },
			{ column: 'bnr_id', aggregateFunction: AggregateFunction.NONE },
			{ column: 'bensl', aggregateFunction: AggregateFunction.NONE },
			{ column: 'action', aggregateFunction: AggregateFunction.NONE },
			{ column: 'action_date', aggregateFunction: AggregateFunction.NONE },
			{ column: 'name', aggregateFunction: AggregateFunction.NONE },
			{ column: 'steart', aggregateFunction: AggregateFunction.NONE },
			{ column: 'vd', aggregateFunction: AggregateFunction.NONE },
			{ column: 'gs', aggregateFunction: AggregateFunction.NONE },
		];

		const results = await this.query(selectColumns, this.filterWhere) as TrackingLogEntry[];
		if (this.filterKey !== keyAtStart) return this.items;
		this.items = results;
		this.loadedForFilter = keyAtStart;
		return this.items;
	}

	@action
	async loadByDay() {
		if (this.byDayLoaded) return this.byDay;

		const keyAtStart = this.filterKey;
		const selectColumns: SelectColumn[] = [
			{ column: 'action_day', aggregateFunction: AggregateFunction.NONE, alias: 'period', groupBy: true },
			...actionCaseCols(AggregateFunction.COUNT),
		];

		const request: AgtAnalyticsRequestDto = {
			select: selectColumns,
			from: this.config.tableName,
			where: this.filterWhere,
			orderBy: 'action_day',
			orderDirection: 'DESC',
		};

		const results = await this.queryWithRequest(request) as TrackingLogAggRow[];
		if (this.filterKey !== keyAtStart) return this.byDay;
		this.byDay = results;
		this.byDayLoadedForFilter = keyAtStart;
		return this.byDay;
	}

	@action
	async loadByMonth() {
		if (this.byMonthLoaded) return this.byMonth;

		const keyAtStart = this.filterKey;
		const selectColumns: SelectColumn[] = [
			{ column: 'action_month', aggregateFunction: AggregateFunction.NONE, alias: 'period', groupBy: true },
			...actionCaseCols(AggregateFunction.COUNT),
		];

		const request: AgtAnalyticsRequestDto = {
			select: selectColumns,
			from: this.config.tableName,
			where: this.filterWhere,
			orderBy: 'action_month',
			orderDirection: 'DESC',
		};

		const results = await this.queryWithRequest(request) as TrackingLogAggRow[];
		if (this.filterKey !== keyAtStart) return this.byMonth;
		this.byMonth = results;
		this.byMonthLoadedForFilter = keyAtStart;
		return this.byMonth;
	}

	@action
	async loadByHour() {
		if (this.byHourLoaded) return this.byHour;

		const keyAtStart = this.filterKey;
		const selectColumns: SelectColumn[] = [
			{ column: 'action_hour', aggregateFunction: AggregateFunction.NONE, alias: 'period', groupBy: true },
			...actionCaseCols(AggregateFunction.COUNT),
		];

		const hourWhere = "action_date >= NOW() - INTERVAL '7 days'";
		const where = this.filterWhere ? `${this.filterWhere} AND ${hourWhere}` : hourWhere;

		const request: AgtAnalyticsRequestDto = {
			select: selectColumns,
			from: this.config.tableName,
			where,
			orderBy: 'action_hour',
			orderDirection: 'ASC',
		};

		const hourResults = await this.queryWithRequest(request) as TrackingLogAggRow[];
		if (this.filterKey !== keyAtStart) return this.byHour;
		this.byHour = hourResults;
		this.byHourLoadedForFilter = keyAtStart;
		return this.byHour;
	}

	@action
	async loadLeaderboard() {
		if (this.leaderboardLoaded) return this.loginFailedLeaderboard;

		const keyAtStart = this.filterKey;
		const selectColumns: SelectColumn[] = [
			{ column: 'bensl', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'name', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'vd', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ caseExpression: { whenCondition: "action_type LIKE 'login_failed%'", thenValue: '1', elseValue: '' }, aggregateFunction: AggregateFunction.COUNT, alias: 'login_fails' },
		];

		const failedWhere = "action_type LIKE 'login_failed%'";
		const where = this.filterWhere ? `${this.filterWhere} AND ${failedWhere}` : failedWhere;

		const request: AgtAnalyticsRequestDto = {
			select: selectColumns,
			from: this.config.tableName,
			where,
			orderBy: 'login_fails',
			orderDirection: 'DESC',
			limit: 50,
		};

		const leaderResults = await this.queryWithRequest(request) as LoginFailedLeaderRow[];
		if (this.filterKey !== keyAtStart) return this.loginFailedLeaderboard;
		this.loginFailedLeaderboard = leaderResults;
		this.leaderboardLoadedForFilter = keyAtStart;
		return this.loginFailedLeaderboard;
	}

	@action
	async loadNavLeaderboard() {
		if (this.navLeaderboardLoaded) return this.navLeaderboard;

		const keyAtStart = this.filterKey;
		const selectColumns: SelectColumn[] = [
			{ column: 'nav_clean', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'nav_clean', aggregateFunction: AggregateFunction.COUNT, alias: 'nav_count' },
		];

		const navWhere = "nav_clean IS NOT NULL";
		const where = this.filterWhere ? `${this.filterWhere} AND ${navWhere}` : navWhere;

		const request: AgtAnalyticsRequestDto = {
			select: selectColumns,
			from: this.config.tableName,
			where,
			orderBy: 'nav_count',
			orderDirection: 'DESC',
			limit: 100,
		};

		const results = await this.queryWithRequest(request) as NavLeaderboardRow[];
		if (this.filterKey !== keyAtStart) return this.navLeaderboard;
		this.navLeaderboard = results;
		this.navLeaderboardLoadedForFilter = keyAtStart;
		return this.navLeaderboard;
	}

	/**
	 * Anzahl Aktionen eines action_type je bnr_id in [dateFrom, dateTo).
	 * Dates must be ISO formatted (YYYY-MM-DD).
	 */
	@action
	async loadActionCountsByBnrId(actionType: string, dateFrom: string, dateTo: string): Promise<BnrActionCountRow[]> {
		const request: AgtAnalyticsRequestDto = {
			select: [
				{ column: 'bnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true },
				{ column: 'bnr_id', aggregateFunction: AggregateFunction.COUNT, alias: 'action_count' },
			],
			from: this.config.tableName,
			where: `action_type = '${sanitize(actionType)}' AND action_date >= '${sanitize(dateFrom)}' AND action_date < '${sanitize(dateTo)}'`,
		};

		return await this.queryWithRequest(request) as BnrActionCountRow[];
	}

	/**
	 * Distinct bnr_ids with at least one login in [dateFrom, dateTo).
	 * Grouped by bnr_id instead of steart, because steart is only tracked in
	 * tracking_log since 6.3.2026 — the steart mapping happens via the user list.
	 * Dates must be ISO formatted (YYYY-MM-DD).
	 */
	@action
	async loadLoginBnrIds(dateFrom: string, dateTo: string): Promise<number[]> {
		const request: AgtAnalyticsRequestDto = {
			select: [{ column: 'bnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true }],
			from: this.config.tableName,
			where: `action_type = 'login' AND action_date >= '${sanitize(dateFrom)}' AND action_date < '${sanitize(dateTo)}'`,
		};

		const results = await this.queryWithRequest(request) as { bnr_id: number }[];
		return results.map((r) => r.bnr_id);
	}

	@action
	async loadActionTypes() {
		if (this.actionTypes.length > 0) return this.actionTypes;

		const request: AgtAnalyticsRequestDto = {
			select: [{ column: 'action_type', aggregateFunction: AggregateFunction.NONE, groupBy: true }],
			from: this.config.tableName,
			orderBy: 'action_type',
			orderDirection: 'ASC',
		};

		const results = await this.queryWithRequest(request) as { action_type: string }[];
		this.actionTypes = results.map((r) => r.action_type);
		return this.actionTypes;
	}

	@action
	reset() {
		this.items = [];
		this.byDay = [];
		this.byMonth = [];
		this.byHour = [];
		this.loginFailedLeaderboard = [];
		this.navLeaderboard = [];
		this.loadedForFilter = '';
		this.byDayLoadedForFilter = '';
		this.byMonthLoadedForFilter = '';
		this.byHourLoadedForFilter = '';
		this.leaderboardLoadedForFilter = '';
		this.navLeaderboardLoadedForFilter = '';
		this.clear();
	}
}
